import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { TOUR_COPY } from "../lib/tutorialSlides";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";

// Lightweight spotlight tour over the project tabs. Switches the active tab
// for each step and highlights the corresponding tab trigger.
export default function ProjectTour({ open, steps, onGoTab, onClose }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[idx];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(`[data-testid="tab-${step.tab}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step]);

  useEffect(() => { if (open) { setIdx(0); } }, [open]);

  useEffect(() => {
    if (!open || !step) return;
    onGoTab(step.tab);
    const t = setTimeout(measure, 220);
    return () => clearTimeout(t);
  }, [open, idx, step, onGoTab, measure]);

  useLayoutEffect(() => {
    if (!open) return;
    const onMove = () => measure();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => { window.removeEventListener("resize", onMove); window.removeEventListener("scroll", onMove, true); };
  }, [open, measure]);

  if (!open || !step) return null;

  const copy = TOUR_COPY[step.tab] || { title: step.label, body: "" };
  const last = idx === steps.length - 1;

  // tooltip placed below the highlighted tab, clamped to viewport
  const tipTop = rect ? Math.min(rect.top + rect.height + 14, window.innerHeight - 220) : 120;
  const tipLeft = rect ? Math.max(16, Math.min(rect.left, window.innerWidth - 360)) : 24;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="project-tour">
      {/* dim with spotlight cutout */}
      {rect ? (
        <div className="absolute pointer-events-none transition-all duration-300"
          style={{
            top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(28,25,22,0.62)", border: "2px solid #A68A6B",
          }} />
      ) : (
        <div className="absolute inset-0 bg-[#1c1916]/60" />
      )}

      {/* tooltip card */}
      <div className="absolute w-[340px] max-w-[calc(100vw-32px)] bg-white border border-[#EBE5E0] shadow-xl"
        style={{ top: tipTop, left: tipLeft }} data-testid="tour-card">
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#A68A6B]">{copy.title}</p>
              {step.highlight && (
                <span className="inline-flex items-center gap-1 bg-[#A68A6B] text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5">
                  <Sparkles size={10} /> Signature
                </span>
              )}
            </div>
            <button data-testid="tour-close" onClick={onClose} className="text-[#5C5651] hover:text-[#9E4747]"><X size={16} /></button>
          </div>
          <p className="text-sm text-[#2B2622] leading-relaxed mt-2">{copy.body}</p>

          <div className="flex items-center justify-between mt-5">
            <span className="text-[11px] uppercase tracking-wider text-[#9b938b]">{idx + 1} / {steps.length}</span>
            <div className="flex items-center gap-2">
              <button data-testid="tour-back" onClick={() => setIdx((p) => Math.max(0, p - 1))} disabled={idx === 0}
                className="p-1.5 text-[#5C5651] hover:text-[#A68A6B] disabled:opacity-30"><ChevronLeft size={18} /></button>
              {last ? (
                <button data-testid="tour-finish" onClick={onClose}
                  className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-[11px] px-4 py-2 transition-colors">
                  Finish
                </button>
              ) : (
                <button data-testid="tour-next" onClick={() => setIdx((p) => Math.min(steps.length - 1, p + 1))}
                  className="inline-flex items-center gap-1 rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-[11px] px-4 py-2 transition-colors">
                  Next <ChevronRight size={15} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
